import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deleteRate, getRateByResortId, getRateCategoryByResortIdResorCategoryId } from "../../../../../../../apis/property";
import CreateRate from "./createRate";
import EditRate from "./editRate";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  TagIcon,
  DocumentTextIcon,
  SearchIcon,
  ViewGridIcon,
  ViewListIcon,
  FilterIcon,
  CollectionIcon,
} from "@heroicons/react/outline";

const Rate = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [rateCategory, setRateCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [rate, setRate] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");

  const fetchRateCategory = async (rateClassId) => {
    try {
      const response = await getRateCategoryByResortIdResorCategoryId(rateClassId);
      console.log(response);
      return response.data.name;
    } catch (error) {
      console.log('Error fetching rate class');
      return '';
    }
  };

  const fetchRate = async () => {
    try {
      const response = await getRateByResortId(id);
      const rates = response.data.rates;
      
      // Fetch rate category names
      const rateCategoryPromises = rates.map(async (rate) => {
        const rateCategory = await fetchRateCategory(rate.rate_category_id);
        return { id: rate.id, rateCategory };
      });

      const rateCategorysResults = await Promise.all(rateCategoryPromises);
      const rateCategorysMap = rateCategorysResults.reduce((acc, { id, rateCategory }) => {
        acc[id] = rateCategory;
        return acc;
      }, {});

      setRateCategory(rateCategorysMap);
      setRate(rates);
      setLoading(false);
    } catch (error) {
      console.log('Error fetching rate');
      setLoading(false);
    }
  };

  // Fetch the initial list when the property page mounts.
  useEffect(() => {
    fetchRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    await fetchRate();
  };

  const openEditModal = (rateId) => {
    setSelectedRateId(rateId);
    setIsEditModalOpen(true);
  };

  const closeEditModal = async () => {
    setIsEditModalOpen(false);
    await fetchRate();
    setSelectedRateId(null);
  };

  const handleDelete = async (rate_id) => {
    if (window.confirm("Are you sure you want to delete this rate?")) {
      try {
        await deleteRate(rate_id);
        toast.success("Rate deleted successfully");
        await fetchRate();
      } catch (error) {
        toast.error("Failed to delete rate");
      }
    }
  };

  // Filter rates based on search
  const filteredRates = rate?.filter((item) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rateCategory[item.id]?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const rateColors = [
    'from-indigo-400 to-indigo-600',
    'from-purple-400 to-purple-600',
    'from-pink-400 to-pink-600',
    'from-rose-400 to-rose-600',
    'from-orange-400 to-orange-600',
  ];

  return (
    <div className="rate-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Rate Configuration</h1>
          <p className="page-subtitle">Manage your property rates and pricing</p>
        </div>
        <button onClick={openModal} className="create-btn">
          <PlusIcon className="w-5 h-5" />
          <span>Add Rate</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card-indigo">
          <div className="stat-icon">
            <CurrencyDollarIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Total Rates</p>
            <h3 className="stat-value">{rate?.length || 0}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-purple">
          <div className="stat-icon">
            <CollectionIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Active Rates</p>
            <h3 className="stat-value">{rate?.length || 0}</h3>
          </div>
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="filters-section">
        <div className="search-wrapper">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search rates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-actions">
          <div className="results-count">
            <span>{filteredRates.length} {filteredRates.length === 1 ? 'Rate' : 'Rates'}</span>
          </div>

          <button className="filter-btn">
            <FilterIcon className="w-4 h-4" />
            <span>Filter</span>
          </button>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content Display */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading rates...</p>
        </div>
      ) : viewMode === "list" ? (
        /* List/Table View */
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" className="checkbox" />
                </th>
                <th>Rate Name</th>
                <th>Code</th>
                <th>Description</th>
                <th>Rate Category</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRates.map((rateItem) => (
                <tr key={rateItem.id}>
                  <td>
                    <input type="checkbox" className="checkbox" />
                  </td>
                  <td>
                    <div className="name-cell">
                      <div className="name-icon">
                        <CurrencyDollarIcon className="w-5 h-5" />
                      </div>
                      <span className="name-text">{rateItem.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="code-badge">{rateItem.code}</span>
                  </td>
                  <td>
                    <span className="description-text">{rateItem.description}</span>
                  </td>
                  <td>
                    <span className="category-badge">{rateCategory[rateItem.id] || 'N/A'}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openEditModal(rateItem.id)}
                        className="action-btn action-btn-edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rateItem.id)}
                        className="action-btn action-btn-delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRates.length === 0 && (
            <div className="empty-state">
              <CurrencyDollarIcon className="w-16 h-16 text-gray-400" />
              <h3>No Rates Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by creating your first rate"}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Grid View */
        <div className="grid-view">
          {filteredRates.map((rateItem, idx) => (
            <div key={rateItem.id} className="rate-card">
              <div className={`rate-card-header bg-gradient-to-br ${rateColors[idx % rateColors.length]}`}>
                <div className="rate-card-icon">
                  <CurrencyDollarIcon className="w-6 h-6" />
                </div>
                <h3 className="rate-card-title">{rateItem.name}</h3>
              </div>
              <div className="rate-card-body">
                <div className="rate-info-item">
                  <div className="info-icon">
                    <TagIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="info-label">Code</p>
                    <p className="info-value">{rateItem.code}</p>
                  </div>
                </div>
                <div className="rate-info-item">
                  <div className="info-icon">
                    <DocumentTextIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="info-label">Description</p>
                    <p className="info-value">{rateItem.description}</p>
                  </div>
                </div>
                <div className="rate-info-item">
                  <div className="info-icon">
                    <CollectionIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="info-label">Category</p>
                    <p className="info-value">{rateCategory[rateItem.id] || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div className="rate-card-footer">
                <button
                  onClick={() => openEditModal(rateItem.id)}
                  className="rate-action-btn rate-edit-btn"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(rateItem.id)}
                  className="rate-action-btn rate-delete-btn"
                >
                  <TrashIcon className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}

          {filteredRates.length === 0 && (
            <div className="empty-state grid-empty">
              <CurrencyDollarIcon className="w-16 h-16 text-gray-400" />
              <h3>No Rates Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by creating your first rate"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateRate isOpen={isModalOpen} onClose={closeModal} />
      <EditRate
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        selectedRateId={selectedRateId}
      />

      {/* Styles */}
      <style jsx>{`
        .rate-page {
          padding: 0;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .create-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.3);
        }

        .create-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.4);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-card-indigo .stat-icon {
          background: linear-gradient(135deg, #6366f1, #4f46e5);
        }

        .stat-card-purple .stat-icon {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .filter-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .results-count {
          padding: 0.5rem 1rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .filter-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-btn:hover {
          border-color: #6366f1;
          color: #6366f1;
        }

        .view-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.5rem;
          padding: 0.25rem;
        }

        .toggle-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          border-radius: 0.375rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-btn.active {
          background: white;
          color: #6366f1;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-state p {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .data-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .data-table tbody tr {
          transition: background 0.2s;
        }

        .data-table tbody tr:hover {
          background: #f9fafb;
        }

        .checkbox {
          width: 1rem;
          height: 1rem;
          border-radius: 0.25rem;
          cursor: pointer;
        }

        .name-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .name-icon {
          width: 2.5rem;
          height: 2.5rem;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .name-text {
          font-weight: 600;
          color: #1f2937;
        }

        .code-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .description-text {
          font-size: 0.875rem;
          color: #6b7280;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .category-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #f3e8ff;
          color: #6b21a8;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: scale(1.1);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .grid-view {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .rate-card {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .rate-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .rate-card-header {
          padding: 1.5rem;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .rate-card-header::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          transform: translate(40%, -40%);
        }

        .rate-card-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .rate-card-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
        }

        .rate-card-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .rate-info-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .info-icon {
          width: 2rem;
          height: 2rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          flex-shrink: 0;
        }

        .info-label {
          font-size: 0.75rem;
          color: #9ca3af;
          margin: 0 0 0.25rem 0;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .info-value {
          font-size: 0.875rem;
          color: #1f2937;
          font-weight: 500;
          margin: 0;
        }

        .rate-card-footer {
          padding: 1rem 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 0.75rem;
        }

        .rate-action-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border-radius: 0.5rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .rate-edit-btn {
          background: #eff6ff;
          color: #2563eb;
        }

        .rate-edit-btn:hover {
          background: #dbeafe;
          transform: translateY(-1px);
        }

        .rate-delete-btn {
          background: #fef2f2;
          color: #dc2626;
        }

        .rate-delete-btn:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 1rem 0 0.5rem 0;
        }

        .empty-state p {
          color: #6b7280;
          margin: 0;
        }

        .grid-empty {
          grid-column: 1 / -1;
        }

        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .create-btn {
            width: 100%;
            justify-content: center;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-wrapper {
            max-width: 100%;
          }

          .filter-actions {
            justify-content: space-between;
          }
        }

        @media (max-width: 768px) {
          .data-table {
            display: block;
            overflow-x: auto;
          }

          .grid-view {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Rate;
